/**
 * MX HOME
 * Collection Slider "RB Inspired"
 *
 * - Navigation avec les flèches
 * - Navigation avec les puces
 * - Swipe tactile mobile
 * - Glissement à la souris
 * - Blocage du lien après un swipe
 * - Compatibilité avec les sections Shopify
 */

(() => {
  'use strict';

  const SELECTORS = {
    slider: '[data-mx-rb-slider]',
    viewport: '[data-mx-rb-slider-viewport]',
    track: '[data-mx-rb-slider-track]',
    slide: '[data-mx-rb-slide]',
    previousButton: '[data-mx-rb-slider-previous]',
    nextButton: '[data-mx-rb-slider-next]',
    dot: '[data-mx-rb-slider-dot]',
    productLink: '.mx-rb-product-slider__link'
  };

  const ACTIVE_CLASS = 'is-active';
  const SWIPE_THRESHOLD = 35;

  class MxRbProductSlider {
    constructor(slider) {
      this.slider = slider;
      this.viewport = slider.querySelector(SELECTORS.viewport);
      this.track = slider.querySelector(SELECTORS.track);
      this.slides = Array.from(
        slider.querySelectorAll(SELECTORS.slide)
      );

      this.previousButton = slider.querySelector(
        SELECTORS.previousButton
      );

      this.nextButton = slider.querySelector(
        SELECTORS.nextButton
      );

      this.dots = Array.from(
        slider.querySelectorAll(SELECTORS.dot)
      );

      this.productLinks = Array.from(
        slider.querySelectorAll(SELECTORS.productLink)
      );

      this.slideCount = this.slides.length;
      this.currentIndex = 0;

      this.pointerId = null;
      this.pointerStartX = 0;
      this.pointerStartY = 0;
      this.pointerCurrentX = 0;
      this.pointerCurrentY = 0;

      this.isPointerDown = false;
      this.isHorizontalGesture = false;
      this.hasSwiped = false;

      if (
        !this.viewport ||
        !this.track ||
        this.slideCount < 1
      ) {
        return;
      }

      this.bindEvents();
      this.goTo(0, false);

      this.slider.dataset.mxRbSliderInitialized = 'true';
    }

    bindEvents() {
      if (this.previousButton) {
        this.previousButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.previous();
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.next();
        });
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const index = Number.parseInt(
            dot.dataset.slideIndex,
            10
          );

          if (!Number.isNaN(index)) {
            this.goTo(index);
          }
        });
      });

      this.viewport.addEventListener(
        'pointerdown',
        this.handlePointerDown.bind(this),
        true
      );

      this.viewport.addEventListener(
        'pointermove',
        this.handlePointerMove.bind(this),
        true
      );

      this.viewport.addEventListener(
        'pointerup',
        this.handlePointerUp.bind(this),
        true
      );

      this.viewport.addEventListener(
        'pointercancel',
        this.handlePointerCancel.bind(this),
        true
      );

      this.productLinks.forEach((link) => {
        link.addEventListener(
          'click',
          (event) => {
            if (!this.hasSwiped) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
          },
          true
        );

        link.addEventListener('dragstart', (event) => {
          event.preventDefault();
        });
      });

      this.slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        }
      });
    }

    handlePointerDown(event) {
      if (
        this.slideCount < 2 ||
        event.isPrimary === false
      ) {
        return;
      }

      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerCurrentX = event.clientX;
      this.pointerCurrentY = event.clientY;

      this.isPointerDown = true;
      this.isHorizontalGesture = false;
      this.hasSwiped = false;

      if (this.viewport.setPointerCapture) {
        try {
          this.viewport.setPointerCapture(event.pointerId);
        } catch (error) {
          // La capture peut être refusée par certains navigateurs.
        }
      }
    }

    handlePointerMove(event) {
      if (
        !this.isPointerDown ||
        event.pointerId !== this.pointerId
      ) {
        return;
      }

      this.pointerCurrentX = event.clientX;
      this.pointerCurrentY = event.clientY;

      const differenceX =
        this.pointerCurrentX - this.pointerStartX;

      const differenceY =
        this.pointerCurrentY - this.pointerStartY;

      if (
        !this.isHorizontalGesture &&
        Math.abs(differenceX) > 8 &&
        Math.abs(differenceX) > Math.abs(differenceY)
      ) {
        this.isHorizontalGesture = true;
        this.slider.classList.add('is-dragging');
      }

      if (!this.isHorizontalGesture) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const viewportWidth =
        this.viewport.getBoundingClientRect().width || 1;

      const basePosition = this.currentIndex * -100;
      const movementPosition =
        (differenceX / viewportWidth) * 100;

      this.track.style.transition = 'none';
      this.track.style.transform =
        `translate3d(${basePosition + movementPosition}%, 0, 0)`;
    }

    handlePointerUp(event) {
      if (
        !this.isPointerDown ||
        event.pointerId !== this.pointerId
      ) {
        return;
      }

      const differenceX =
        event.clientX - this.pointerStartX;

      const differenceY =
        event.clientY - this.pointerStartY;

      const validSwipe =
        this.isHorizontalGesture &&
        Math.abs(differenceX) >= SWIPE_THRESHOLD &&
        Math.abs(differenceX) > Math.abs(differenceY);

      this.resetPointerState();

      if (!validSwipe) {
        this.goTo(this.currentIndex);
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.hasSwiped = true;

      if (differenceX < 0) {
        this.next();
      } else {
        this.previous();
      }

      window.setTimeout(() => {
        this.hasSwiped = false;
      }, 500);
    }

    handlePointerCancel(event) {
      if (event.pointerId !== this.pointerId) {
        return;
      }

      this.resetPointerState();
      this.goTo(this.currentIndex);
    }

    resetPointerState() {
      this.isPointerDown = false;
      this.isHorizontalGesture = false;
      this.pointerId = null;

      this.slider.classList.remove('is-dragging');
      this.track.style.transition = '';
    }

    previous() {
      const previousIndex =
        this.currentIndex === 0
          ? this.slideCount - 1
          : this.currentIndex - 1;

      this.goTo(previousIndex);
    }

    next() {
      const nextIndex =
        this.currentIndex === this.slideCount - 1
          ? 0
          : this.currentIndex + 1;

      this.goTo(nextIndex);
    }

    goTo(index, animate = true) {
      if (this.slideCount < 1) {
        return;
      }

      const normalizedIndex =
        ((index % this.slideCount) + this.slideCount) %
        this.slideCount;

      this.currentIndex = normalizedIndex;

      this.track.style.transition = animate ? '' : 'none';

      this.track.style.transform =
        `translate3d(-${normalizedIndex * 100}%, 0, 0)`;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;

        const link = slide.querySelector(
          SELECTORS.productLink
        );

        slide.classList.toggle(ACTIVE_CLASS, isActive);

        slide.setAttribute(
          'aria-hidden',
          isActive ? 'false' : 'true'
        );

        if (link) {
          link.tabIndex = isActive ? 0 : -1;
        }
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === normalizedIndex;

        dot.classList.toggle(ACTIVE_CLASS, isActive);

        dot.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });

      if (!animate) {
        window.requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }
    }
  }

  function initializeMxRbSliders(container = document) {
    const sliders = container.querySelectorAll(
      SELECTORS.slider
    );

    sliders.forEach((slider) => {
      if (
        slider.dataset.mxRbSliderInitialized === 'true'
      ) {
        return;
      }

      new MxRbProductSlider(slider);
    });
  }

  function initializeDocument() {
    initializeMxRbSliders(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeDocument,
      { once: true }
    );
  } else {
    initializeDocument();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeMxRbSliders(event.target);
    }
  );
})();